import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";
import { useCallback, useMemo } from "react";
import Supercluster from "supercluster";
import type {
    AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    MapEmissionRecordsListEventStatusEnum,
    MapEmissionRecordsListExcludeEventStatusEnum,
    MapEmissionRecordsListRequest,
    MapPlumeImagesListDataSourceEnum,
    MapPlumeOutlinesListRequest,
    MapUnmatchedDataPlumesListRequest,
    MapUnmatchedDataPointsListRequest,
    PlumeImage,
    PlumeOulines,
} from "../../../apiClient/generated";
import { useUser } from "../../../auth";
import { useMapApiClient } from "../../../hooks";
import { createFeatureCollection } from "../../../utils/geopatialUtils";
import type {
    DataPointMapWithBin,
    EmissionRecordMapWithBin,
} from "../CustomTypes";
import {
    getBinForRateOrConcentration,
    MAP_ZOOM_SHOW_DETAILS,
} from "../constants";
import type { FilterByAreaDef } from "../hooks";
import {
    type EmissionLayerStyle,
    EmissionRecordsLayers,
    PlumeOutlinesLayer,
} from "../layers/emissions";
import { RasterLayer } from "../layers/rasters";
import { useStable } from "./areaFilter";
import { useMapDataLoader } from "./dataLoader";
import { useMapData } from "./mapDataAndFilters";
import { useMap } from "./mapState";
import { cleanFilters } from "./utils";

export type EmissionFilters = {
    detectionDateRangeAfter?: Date;
    detectionDateRangeBefore?: Date;
    emissionMatchedAfter?: Date;
    emissionMatchedBefore?: Date;
    providerWithSource?:
        | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
        | "all";
    dataSource?: MapPlumeImagesListDataSourceEnum[];
    excludeEventStatus?: MapEmissionRecordsListExcludeEventStatusEnum[];
    detectedRateMax?: number;
    detectedRateMin?: number;
    site?: string;
    eventStatus?: string[];
    relatedSite?: string;
};

/**
 * Emission records: custom hook that loads emissions-related
 * data (points and plume images) based on filter values and
 * returns map layers for display.
 */
export const useEmissionRecordsOnMap = (
    mapId: string,
    enabled: boolean,
    dataName: string,
    enableOverviews: boolean,
    filters: EmissionFilters,
    layerStyle: EmissionLayerStyle,
    filterByArea?: FilterByAreaDef,
    filterEmissionPoints?: boolean,
) => {
    const apiClient = useMapApiClient();
    const isSuperuser = useUser().isSuperuser;
    const stableMapId = useStable(mapId);
    const {
        debounced: { viewState, areaOnScreen },
    } = useMap(stableMapId);
    const {
        selectedContext,
        mapSettings: { plumeOpacity, showPlumes },
    } = useMapData(stableMapId);
    const currentZoom = useMemo(() => viewState?.zoom, [viewState]);

    const emissionsDataLoader = useMapDataLoader<EmissionRecordMapWithBin>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<EmissionFilters>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as EmissionRecordMapWithBin[],
                    };
                }
                const response = await apiClient.mapEmissionRecordsList(
                    {
                        ...cleanFilterValues,
                        site: cleanFilterValues.site
                            ? [cleanFilterValues.site]
                            : undefined,
                        relatedSite: cleanFilterValues.relatedSite,
                        createdAtAfter: cleanFilterValues.emissionMatchedAfter,
                        eventStatus: cleanFilterValues.eventStatus
                            ? (cleanFilterValues.eventStatus as MapEmissionRecordsListEventStatusEnum[])
                            : undefined,
                        createdAtBefore:
                            cleanFilterValues.emissionMatchedBefore,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
                return {
                    ...response,
                    results: response.results.map((i) => {
                        return {
                            ...i,
                            mapDotSize: getBinForRateOrConcentration(
                                i.detectedRate / 1000,
                                i.concentration,
                            ).size,
                        };
                    }),
                };
            },
            [filters, filterByArea, apiClient],
        ),
        zoomToStartFetching: 1,
        enabled:
            enabled &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all"),
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
    });

    const plumeImagesDataLoader = useMapDataLoader<PlumeImage>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<EmissionFilters>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as PlumeImage[],
                    };
                }
                return await apiClient.mapPlumeImagesList(
                    {
                        ...cleanFilterValues,
                        excludeEventStatus: isSuperuser
                            ? undefined
                            : cleanFilterValues.excludeEventStatus,
                        eventStatus: cleanFilterValues.eventStatus
                            ? (cleanFilterValues.eventStatus as MapEmissionRecordsListEventStatusEnum[])
                            : undefined,
                        site: cleanFilterValues.site
                            ? [cleanFilterValues.site]
                            : undefined,
                        relatedSite: cleanFilterValues.relatedSite,
                        emissionCreatedAtAfter:
                            cleanFilterValues.emissionMatchedAfter,
                        emissionCreatedAtBefore:
                            cleanFilterValues.emissionMatchedBefore,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [filters, filterByArea, apiClient, isSuperuser],
        ),
        zoomToStartFetching: MAP_ZOOM_SHOW_DETAILS,
        enabled:
            enabled &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all") &&
            showPlumes,
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
        unloadZoomLevel: MAP_ZOOM_SHOW_DETAILS - 1,
    });

    const plumeOutlinesDataLoader = useMapDataLoader<PlumeOulines>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<EmissionFilters>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as PlumeOulines[],
                    };
                }
                return await apiClient.mapPlumeOutlinesList(
                    {
                        ...cleanFilterValues,
                        excludeEventStatus: isSuperuser
                            ? undefined
                            : cleanFilterValues.excludeEventStatus,
                        site: cleanFilterValues.site
                            ? [cleanFilterValues.site]
                            : undefined,
                        relatedSite: cleanFilterValues.relatedSite,
                        emissionCreatedAtAfter:
                            cleanFilterValues.emissionMatchedAfter,
                        emissionCreatedAtBefore:
                            cleanFilterValues.emissionMatchedBefore,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        eventStatus: cleanFilterValues.eventStatus
                            ? (cleanFilterValues.eventStatus as MapEmissionRecordsListEventStatusEnum[])
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [filters, filterByArea, apiClient, isSuperuser],
        ),
        zoomToStartFetching: MAP_ZOOM_SHOW_DETAILS,
        enabled:
            enabled &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all"),
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
        unloadZoomLevel: MAP_ZOOM_SHOW_DETAILS - 1,
    });

    const emissionData = useMemo(() => {
        if (filterEmissionPoints && selectedContext.emissionRecordId) {
            return emissionsDataLoader.data.filter(
                (e) => e.id === selectedContext.emissionRecordId,
            );
        }
        if (filterEmissionPoints && selectedContext.emissionRecordIds) {
            return emissionsDataLoader.data.filter((e) =>
                selectedContext.emissionRecordIds.includes(e.id),
            );
        }
        if (
            selectedContext.dateRangeToShowData?.start &&
            selectedContext.dateRangeToShowData?.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            return emissionsDataLoader.data.filter((e) => {
                const dateToCompare = filters.emissionMatchedAfter
                    ? e.createdAt
                    : e.detectionTimestamp;

                return (
                    (!startDate || (startDate && dateToCompare >= startDate)) &&
                    (!endDate || (endDate && dateToCompare <= endDate))
                );
            });
        }
        return emissionsDataLoader.data || [];
    }, [
        emissionsDataLoader.data,
        selectedContext.dateRangeToShowData,
        selectedContext.emissionRecordId,
        selectedContext.emissionRecordIds,
        filters.emissionMatchedAfter,
        filterEmissionPoints,
    ]);

    // Set up emission clustering
    const clusterData = useMemo(() => {
        const index = new Supercluster({
            radius: 50,
            maxZoom: 9,
            map: (props) => ({
                highestRate: props.detectedRate,
                highestConcentration: props.concentration,
            }),
            reduce: (accumulated, props) => {
                accumulated.highestRate = Math.max(
                    accumulated.highestRate,
                    props.highestRate,
                );
                accumulated.highestConcentration = Math.max(
                    accumulated.highestConcentration,
                    props.highestConcentration,
                );
            },
        });
        index.load(
            emissionData.map(
                (i) =>
                    ({
                        type: "Feature",
                        geometry: i.location,
                        properties: i,
                    }) as any,
            ),
        );
        return index;
    }, [emissionData]);

    const clusteredPoints = useMemo(() => {
        if (!areaOnScreen) {
            return [];
        }

        if (currentZoom >= 9) {
            return [];
        }

        // Annotate zoom level in which cluster is broken down.
        return clusterData
            .getClusters([-180, -90, 180, 90], currentZoom)
            .map((i: any) => {
                if (i.properties.cluster) {
                    i.properties.zoom = clusterData.getClusterExpansionZoom(
                        i.properties.cluster_id,
                    );
                    i.properties.tooltip = "Click to zoom in";
                    i.properties.mapDotSize = getBinForRateOrConcentration(
                        i.properties.highestRate / 1000,
                        i.properties.highestConcentration,
                    ).size;
                    i.properties.getEmissionIds = (): string[] =>
                        clusterData
                            .getLeaves(i.properties.cluster_id, Infinity)
                            .map((leaf) => leaf.properties.id);
                }
                return i;
            });
    }, [clusterData, currentZoom, areaOnScreen]);

    // Computes data on screen
    const dataOnScreen = useMemo(() => {
        if (!areaOnScreen) {
            return [];
        }
        return emissionsDataLoader.data.filter((e) =>
            booleanPointInPolygon(e.location, areaOnScreen),
        );
    }, [emissionsDataLoader.data, areaOnScreen]);

    // Computes data on screen
    const dataFilteredByContext = useMemo(() => {
        if (!areaOnScreen) {
            return [];
        }
        return emissionData.filter((e) =>
            booleanPointInPolygon(e.location, areaOnScreen),
        );
    }, [emissionData, areaOnScreen]);

    // Layers
    const emissionLayers = useMemo(() => {
        return [
            EmissionRecordsLayers(
                createFeatureCollection(emissionData).features,
                enabled,
                currentZoom,
                dataName,
                layerStyle,
                // Only send clustered data if overviews are enabled.
                enableOverviews ? clusteredPoints : undefined,
            ),
        ];
    }, [
        emissionData,
        enabled,
        currentZoom,
        layerStyle,
        dataName,
        enableOverviews,
        clusteredPoints,
    ]);

    // Filter plume data: when `dateRangeToShowData` is set,
    // it should only display data for that range
    const plumeOutlineData = useMemo(() => {
        if (selectedContext.emissionRecordIds) {
            const ids = selectedContext.emissionRecordIds;
            return plumeOutlinesDataLoader.data.filter((o) =>
                o.emissionRecordIds.some((eid) => ids.includes(eid)),
            );
        }
        if (
            selectedContext.dateRangeToShowData?.start &&
            selectedContext.dateRangeToShowData?.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            return plumeOutlinesDataLoader.data.filter((e) => {
                const datesToCompare = filters.emissionMatchedAfter
                    ? e.matchedAt
                    : [e.detectionTimestamp];

                return datesToCompare.some(
                    (dateToCompare) =>
                        (!startDate || dateToCompare >= startDate) &&
                        (!endDate || dateToCompare <= endDate),
                );
            });
        }
        return plumeOutlinesDataLoader.data || [];
    }, [
        plumeOutlinesDataLoader.data,
        filters.emissionMatchedAfter,
        selectedContext.dateRangeToShowData,
        selectedContext.emissionRecordIds,
    ]);

    const plumeImageData = useMemo(() => {
        if (selectedContext.plumeIds) {
            return plumeImagesDataLoader.data.filter((e) =>
                selectedContext.plumeIds.includes(e.id),
            );
        }
        if (
            selectedContext.dateRangeToShowData?.start &&
            selectedContext.dateRangeToShowData?.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            return plumeImagesDataLoader.data.filter((e) => {
                const datesToCompare = filters.emissionMatchedAfter
                    ? e.matchedAt
                    : [e.takenAt];

                return datesToCompare.some(
                    (dateToCompare) =>
                        (!startDate || dateToCompare >= startDate) &&
                        (!endDate || dateToCompare <= endDate),
                );
            });
        }
        return plumeImagesDataLoader.data || [];
    }, [
        plumeImagesDataLoader.data,
        selectedContext.dateRangeToShowData,
        selectedContext.plumeIds,
        filters.emissionMatchedAfter,
    ]);

    const plumeLayers = useMemo(() => {
        const layers = [];
        const visiblePlumeOutlines = plumeOutlineData.filter((outline) => {
            if (showPlumes && outline.hasPlume) {
                return false;
            }
            if (selectedContext.dataPointId) {
                return selectedContext.dataPointId === outline.id;
            }
            if (selectedContext.emissionRecordId) {
                return outline.emissionRecordIds.some(
                    (i) => i === selectedContext.emissionRecordId,
                );
            }
            return true;
        });

        // Plume outlines first, so they render at the bottom
        if (visiblePlumeOutlines.length > 0) {
            layers.push(
                PlumeOutlinesLayer(
                    visiblePlumeOutlines,
                    `plume_outlines_${dataName}`,
                    enabled,
                    currentZoom,
                    plumeOpacity,
                    !!selectedContext.relatedPlume,
                ),
            );
        }

        // Then plume images, only if they are enabled
        if (selectedContext.relatedPlume !== null && showPlumes) {
            layers.push(
                RasterLayer({
                    data: selectedContext.relatedPlume
                        ? plumeImageData.filter(
                              (i) => i.id === selectedContext.relatedPlume,
                          )
                        : plumeImageData,
                    name: `plumes_${dataName}`,
                    visible: enabled,
                    zoomLevel: currentZoom,
                    opacity: plumeOpacity,
                    detail: !!selectedContext.relatedPlume,
                }),
            );
        }
        return layers;
    }, [
        enabled,
        plumeImageData,
        plumeOutlineData,
        currentZoom,
        selectedContext,
        plumeOpacity,
        showPlumes,
        dataName,
    ]);

    return {
        loading: emissionsDataLoader.loading || plumeImagesDataLoader.loading,
        data: emissionsDataLoader.data,
        dataOnScreen,
        dataFilteredByContext,
        emissionLayers,
        plumeLayers,
    };
};

/**
 * Public data points.
 *
 * At this point this is just a copy and paste of the method above
 * minus the clustering code and with changes to accomodate
 * the DataPoint model.
 *
 * // FIXME: Build a re-usable thing and remove duplication.
 */
export const usePublicDataPointsOnMap = (
    enabled: boolean,
    dataName: string,
    filters: {
        detectionDateRangeAfter?: Date;
        detectionDateRangeBefore?: Date;
        providerWithSource?:
            | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
            | "all";
        dataSource?: MapPlumeImagesListDataSourceEnum[];
        detectedRateMax?: number;
        detectedRateMin?: number;
    },
    layerStyle: EmissionLayerStyle,
    filterByArea?: FilterByAreaDef,
) => {
    const apiClient = useMapApiClient();
    const {
        debounced: { viewState, areaOnScreen },
    } = useMap("mainMap");
    const {
        selectedContext,
        mapSettings: { plumeOpacity, showPlumes },
    } = useMapData("mainMap");
    const currentZoom = useMemo(() => viewState?.zoom, [viewState]);

    const emissionsDataLoader = useMapDataLoader<DataPointMapWithBin>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<MapEmissionRecordsListRequest>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as DataPointMapWithBin[],
                    };
                }
                const response = await apiClient.mapPublicEmissionsList(
                    {
                        ...cleanFilterValues,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
                return {
                    ...response,
                    results: response.results.map((i) => {
                        return {
                            ...i,
                            mapDotSize: getBinForRateOrConcentration(
                                i.detectedRate / 1000,
                                i.concentration,
                            ).size,
                        };
                    }),
                };
            },
            [filters, filterByArea, apiClient],
        ),
        zoomToStartFetching: 1,
        enabled:
            enabled &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all"),
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
    });

    const plumeImagesDataLoader = useMapDataLoader<PlumeImage>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<MapEmissionRecordsListRequest>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as PlumeImage[],
                    };
                }
                return await apiClient.mapPublicPlumeImagesList(
                    {
                        ...cleanFilterValues,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [filters, filterByArea, apiClient],
        ),
        zoomToStartFetching: MAP_ZOOM_SHOW_DETAILS,
        enabled:
            enabled &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all") &&
            showPlumes,
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
        unloadZoomLevel: MAP_ZOOM_SHOW_DETAILS - 1,
    });

    const plumeOutlinesDataLoader = useMapDataLoader<PlumeOulines>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<MapPlumeOutlinesListRequest>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as PlumeOulines[],
                    };
                }
                return await apiClient.mapPublicPlumeOutlinesList(
                    {
                        ...cleanFilterValues,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [filters, filterByArea, apiClient],
        ),
        zoomToStartFetching: MAP_ZOOM_SHOW_DETAILS,
        enabled:
            enabled &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all"),
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
        unloadZoomLevel: MAP_ZOOM_SHOW_DETAILS - 1,
    });

    const emissionData = useMemo(() => {
        if (
            selectedContext.dateRangeToShowData &&
            selectedContext.dateRangeToShowData.start &&
            selectedContext.dateRangeToShowData.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            return emissionsDataLoader.data.filter(
                (e) =>
                    (!startDate ||
                        (startDate && e.detectionTimestamp >= startDate)) &&
                    (!endDate || (endDate && e.detectionTimestamp <= endDate)),
            );
        }
        return emissionsDataLoader.data || [];
    }, [emissionsDataLoader.data, selectedContext.dateRangeToShowData]);

    // Computes data on screen
    const dataOnScreen = useMemo(() => {
        if (!areaOnScreen) {
            return [];
        }
        return emissionsDataLoader.data.filter((e) =>
            booleanPointInPolygon(e.location, areaOnScreen),
        );
    }, [emissionsDataLoader.data, areaOnScreen]);

    // Computes data on screen
    const dataFilteredByContext = useMemo(() => {
        if (!areaOnScreen) {
            return [];
        }
        return emissionData.filter((e) =>
            booleanPointInPolygon(e.location, areaOnScreen),
        );
    }, [emissionData, areaOnScreen]);

    // Layers
    const emissionLayers = useMemo(() => {
        return [
            EmissionRecordsLayers(
                createFeatureCollection(emissionData).features,
                enabled,
                currentZoom,
                dataName,
                layerStyle,
                undefined,
            ),
        ];
    }, [emissionData, enabled, currentZoom, layerStyle, dataName]);

    // Filter plume data: when `dateRangeToShowData` is set,
    // it should only display data for that range
    const plumeOutlineData = useMemo(() => {
        if (
            selectedContext.dateRangeToShowData &&
            selectedContext.dateRangeToShowData.start &&
            selectedContext.dateRangeToShowData.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            return plumeOutlinesDataLoader.data.filter(
                (e) =>
                    (!startDate ||
                        (startDate && e.detectionTimestamp >= startDate)) &&
                    (!endDate || (endDate && e.detectionTimestamp <= endDate)),
            );
        }
        return plumeOutlinesDataLoader.data || [];
    }, [plumeOutlinesDataLoader.data, selectedContext.dateRangeToShowData]);

    const plumeImageData = useMemo(() => {
        if (
            selectedContext.dateRangeToShowData &&
            selectedContext.dateRangeToShowData.start &&
            selectedContext.dateRangeToShowData.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            return plumeImagesDataLoader.data.filter(
                (e) =>
                    (!startDate || (startDate && e.takenAt >= startDate)) &&
                    (!endDate || (endDate && e.takenAt <= endDate)),
            );
        }
        return plumeImagesDataLoader.data || [];
    }, [plumeImagesDataLoader.data, selectedContext.dateRangeToShowData]);

    const plumeLayers = useMemo(() => {
        const layers = [];
        const visiblePlumeOutlines = plumeOutlineData.filter((outline) => {
            if (showPlumes && outline.hasPlume) {
                return false;
            }
            if (selectedContext.dataPointId) {
                return selectedContext.dataPointId === outline.id;
            }
            if (selectedContext.emissionRecordId) {
                return outline.emissionRecordIds.some(
                    (i) => i === selectedContext.emissionRecordId,
                );
            }
            return true;
        });

        // Plume outlines first, so they render at the bottom
        if (visiblePlumeOutlines.length > 0) {
            layers.push(
                PlumeOutlinesLayer(
                    visiblePlumeOutlines,
                    `plume_outlines_${dataName}`,
                    enabled,
                    currentZoom,
                    plumeOpacity,
                    !!selectedContext.relatedPlume,
                ),
            );
        }

        // Then plume images, only if they are enabled
        if (selectedContext.relatedPlume !== null && showPlumes) {
            layers.push(
                RasterLayer({
                    data: selectedContext.relatedPlume
                        ? plumeImageData.filter(
                              (i) => i.id === selectedContext.relatedPlume,
                          )
                        : plumeImageData,
                    name: `plumes_${dataName}`,
                    visible: enabled,
                    zoomLevel: currentZoom,
                    opacity: plumeOpacity,
                    detail: !!selectedContext.relatedPlume,
                }),
            );
        }
        return layers;
    }, [
        enabled,
        plumeImageData,
        plumeOutlineData,
        currentZoom,
        selectedContext,
        plumeOpacity,
        showPlumes,
        dataName,
    ]);

    return {
        loading: emissionsDataLoader.loading || plumeImagesDataLoader.loading,
        data: emissionsDataLoader.data,
        dataOnScreen,
        dataFilteredByContext,
        emissionLayers,
        plumeLayers,
    };
};

/**
 * Unmatched data points.
 *
 * // FIXME: Build a re-usable thing and remove duplication.
 */
export const useUnmatchedDataPointsOnMap = (
    enabled: boolean,
    dataName: string,
    filters: {
        detectionDateRangeAfter?: Date;
        detectionDateRangeBefore?: Date;
        providerWithSource?:
            | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
            | "all";
        dataSource?: MapPlumeImagesListDataSourceEnum[];
        detectedRateMax?: number;
        detectedRateMin?: number;
    },
    layerStyle: EmissionLayerStyle,
    filterByArea?: FilterByAreaDef,
) => {
    const apiClient = useMapApiClient();
    const {
        debounced: { viewState, areaOnScreen },
    } = useMap("mainMap");
    const {
        selectedContext,
        mapSettings: { plumeOpacity, showPlumes },
    } = useMapData("mainMap");
    const currentZoom = useMemo(() => viewState?.zoom, [viewState]);

    const emissionsDataLoader = useMapDataLoader<DataPointMapWithBin>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<MapUnmatchedDataPointsListRequest>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as DataPointMapWithBin[],
                    };
                }
                const response = await apiClient.mapUnmatchedDataPointsList(
                    {
                        ...cleanFilterValues,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
                return {
                    ...response,
                    results: response.results.map((i) => {
                        return {
                            ...i,
                            mapDotSize: getBinForRateOrConcentration(
                                i.detectedRate / 1000,
                                i.concentration,
                            ).size,
                        };
                    }),
                };
            },
            [filters, filterByArea, apiClient],
        ),
        zoomToStartFetching: 1,
        enabled:
            enabled &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all"),
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
    });

    const plumeImagesDataLoader = useMapDataLoader<PlumeImage>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<MapUnmatchedDataPlumesListRequest>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as PlumeImage[],
                    };
                }
                return await apiClient.mapUnmatchedDataPlumesList(
                    {
                        ...cleanFilterValues,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [filters, filterByArea, apiClient],
        ),
        zoomToStartFetching: MAP_ZOOM_SHOW_DETAILS,
        enabled:
            enabled &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all") &&
            showPlumes,
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
        unloadZoomLevel: MAP_ZOOM_SHOW_DETAILS - 1,
    });

    const plumeOutlinesDataLoader = useMapDataLoader<PlumeOulines>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<MapPlumeOutlinesListRequest>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as PlumeOulines[],
                    };
                }
                return await apiClient.mapUnmatchedDataPlumeOutlinesList(
                    {
                        ...cleanFilterValues,
                        providerWithSource: cleanFilterValues.providerWithSource
                            ? (JSON.stringify(
                                  cleanFilterValues.providerWithSource,
                              ) as any)
                            : undefined,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
            },
            [filters, filterByArea, apiClient],
        ),
        zoomToStartFetching: MAP_ZOOM_SHOW_DETAILS,
        enabled:
            enabled &&
            (filters.providerWithSource.length > 0 ||
                filters.providerWithSource === "all"),
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
        unloadZoomLevel: MAP_ZOOM_SHOW_DETAILS - 1,
    });

    const emissionData = useMemo(() => {
        if (
            selectedContext.dateRangeToShowData &&
            selectedContext.dateRangeToShowData.start &&
            selectedContext.dateRangeToShowData.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            return emissionsDataLoader.data.filter(
                (e) =>
                    (!startDate ||
                        (startDate && e.detectionTimestamp >= startDate)) &&
                    (!endDate || (endDate && e.detectionTimestamp <= endDate)),
            );
        }
        return emissionsDataLoader.data || [];
    }, [emissionsDataLoader.data, selectedContext.dateRangeToShowData]);

    // Computes data on screen
    const dataOnScreen = useMemo(() => {
        if (!areaOnScreen) {
            return [];
        }
        return emissionsDataLoader.data.filter((e) =>
            booleanPointInPolygon(e.location, areaOnScreen),
        );
    }, [emissionsDataLoader.data, areaOnScreen]);

    // Computes data on screen
    const dataFilteredByContext = useMemo(() => {
        if (!areaOnScreen) {
            return [];
        }
        return emissionData.filter((e) =>
            booleanPointInPolygon(e.location, areaOnScreen),
        );
    }, [emissionData, areaOnScreen]);

    // Layers
    const emissionLayers = useMemo(() => {
        return [
            EmissionRecordsLayers(
                createFeatureCollection(emissionData).features,
                enabled,
                currentZoom,
                dataName,
                layerStyle,
                undefined,
            ),
        ];
    }, [emissionData, enabled, currentZoom, layerStyle, dataName]);

    // Filter plume data: when `dateRangeToShowData` is set,
    // it should only display data for that range
    const plumeOutlineData = useMemo(() => {
        if (
            selectedContext.dateRangeToShowData &&
            selectedContext.dateRangeToShowData.start &&
            selectedContext.dateRangeToShowData.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            return plumeOutlinesDataLoader.data.filter(
                (e) =>
                    (!startDate ||
                        (startDate && e.detectionTimestamp >= startDate)) &&
                    (!endDate || (endDate && e.detectionTimestamp <= endDate)),
            );
        }
        return plumeOutlinesDataLoader.data || [];
    }, [plumeOutlinesDataLoader.data, selectedContext.dateRangeToShowData]);

    const plumeImageData = useMemo(() => {
        if (
            selectedContext.dateRangeToShowData &&
            selectedContext.dateRangeToShowData.start &&
            selectedContext.dateRangeToShowData.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            return plumeImagesDataLoader.data.filter(
                (e) =>
                    (!startDate || (startDate && e.takenAt >= startDate)) &&
                    (!endDate || (endDate && e.takenAt <= endDate)),
            );
        }
        return plumeImagesDataLoader.data || [];
    }, [plumeImagesDataLoader.data, selectedContext.dateRangeToShowData]);

    const plumeLayers = useMemo(() => {
        const layers = [];
        const visiblePlumeOutlines = plumeOutlineData.filter((outline) => {
            if (showPlumes && outline.hasPlume) {
                return false;
            }
            if (selectedContext.unmatchedDataPointId) {
                return selectedContext.unmatchedDataPointId === outline.id;
            }
            if (selectedContext.emissionRecordId) {
                return outline.emissionRecordIds.some(
                    (i) => i === selectedContext.emissionRecordId,
                );
            }
            return true;
        });

        // Plume outlines first, so they render at the bottom
        if (visiblePlumeOutlines.length > 0) {
            layers.push(
                PlumeOutlinesLayer(
                    visiblePlumeOutlines,
                    `unmatched_plume_outlines_${dataName}`,
                    enabled,
                    currentZoom,
                    plumeOpacity,
                    !!selectedContext.relatedPlume,
                ),
            );
        }

        // Then plume images, only if they are enabled
        if (selectedContext.relatedPlume !== null && showPlumes) {
            layers.push(
                RasterLayer({
                    data: selectedContext.relatedPlume
                        ? plumeImageData.filter(
                              (i) => i.id === selectedContext.relatedPlume,
                          )
                        : plumeImageData,
                    name: `unmatched_plumes_${dataName}`,
                    visible: enabled,
                    zoomLevel: currentZoom,
                    opacity: plumeOpacity,
                    detail: !!selectedContext.relatedPlume,
                }),
            );
        }
        return layers;
    }, [
        enabled,
        plumeImageData,
        plumeOutlineData,
        currentZoom,
        selectedContext,
        plumeOpacity,
        showPlumes,
        dataName,
    ]);

    return {
        loading: emissionsDataLoader.loading || plumeImagesDataLoader.loading,
        data: emissionsDataLoader.data,
        dataOnScreen,
        dataFilteredByContext,
        emissionLayers,
        plumeLayers,
    };
};
